import React from "react";

/**
 * TallyPot — "Set B / Minimal" navigation icon set.
 *
 * Custom 24×24 line icons (1.5px stroke, round caps) that replace the stock
 * lucide-react nav icons. Each icon renders as an outline by default and as a
 * SOLID glyph when `filled` is true — the active-tab treatment paired with the
 * MenuBar's tinted tile + underline dot.
 *
 * Props (all optional):
 *   size        number   px, default 24
 *   color       string   stroke / fill color, default "currentColor"
 *   filled      boolean  solid active treatment, default false
 *   strokeWidth number   default 1.5 (Ranking bars use a fixed 2.2)
 *   bg          string   knock-out color behind solid glyphs; defaults to the
 *                        app background so it tracks light/dark themes.
 */

function Svg({ size = 24, color = "currentColor", strokeWidth = 1.5, fill = "none", filled, bg, children, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

const c = (p) => p.color || "currentColor";
const KNOCKOUT = "var(--background)";

export function HomeIcon(p = {}) {
  const f = p.filled ? c(p) : "none";
  return (
    <Svg {...p} fill={f}>
      <path d="M4.8 10.5 12 4.8l7.2 5.7V19a1 1 0 0 1-1 1H5.8a1 1 0 0 1-1-1z" />
    </Svg>
  );
}

export function RankingIcon(p = {}) {
  // ascending bars — always 2.2 stroke; color brightens when active
  return (
    <Svg {...p} strokeWidth={2.2} fill="none">
      <path d="M6.5 20v-4M12 20v-8.5M17.5 20v-12" />
    </Svg>
  );
}

export function NewGameIcon(p = {}) {
  const bg = p.bg || KNOCKOUT;
  return (
    <Svg {...p} fill="none">
      <circle cx="12" cy="12" r="8.2" fill={p.filled ? c(p) : "none"} />
      <path d="M12 8.3v7.4M8.3 12h7.4" stroke={p.filled ? bg : c(p)} strokeWidth={p.filled ? 2 : 1.5} />
    </Svg>
  );
}

export function SessionsIcon(p = {}) {
  const bg = p.bg || KNOCKOUT;
  return (
    <Svg {...p} fill="none">
      <circle cx="12" cy="12" r="8.2" fill={p.filled ? c(p) : "none"} />
      <path d="M12 7.6v4.6l3 1.8" stroke={p.filled ? bg : c(p)} />
    </Svg>
  );
}

export function ProfileIcon(p = {}) {
  const f = p.filled ? c(p) : "none";
  return (
    <Svg {...p} fill={f}>
      <circle cx="12" cy="9" r="3.4" />
      <path d="M5.6 19.2a6.4 6.4 0 0 1 12.8 0" />
    </Svg>
  );
}

export function LiveGameIcon(p = {}) {
  // "Set A · detailed" live mark — a pulsing broadcast pulse used in place of
  // the New Game glyph while a session is in progress. Always emerald so the
  // ongoing game reads as live regardless of whether the tab is active.
  const size = p.size || 24;
  const color = "#34D399";
  const ring = Math.round(size * 0.92);
  const core = Math.round(size * 0.34);
  return (
    <span className="rr-live-pulse" style={{ width: size, height: size }} aria-hidden="true">
      <span className="rr-live-ring" style={{ width: ring, height: ring, borderColor: color }} />
      <span className="rr-live-ring rr-live-ring--delay" style={{ width: ring, height: ring, borderColor: color }} />
      <span className="rr-live-core" style={{ width: core, height: core, background: color }} />
    </span>
  );
}

export function JackpotIcon(p = {}) {
  // gold accent lives at the call site (pass color="#FBBF24" when active)
  const f = p.filled ? c(p) : "none";
  return (
    <Svg {...p} fill={f}>
      <path d="M12 4.2 4.6 12 12 19.8 19.4 12z" />
    </Svg>
  );
}

export function SettingsIcon(p = {}) {
  const knob = p.filled ? c(p) : "none";
  return (
    <Svg {...p} fill="none">
      <path d="M8.5 5v5M8.5 14v5M15.5 5v3M15.5 12v7" />
      <circle cx="8.5" cy="12" r="2" fill={knob} />
      <circle cx="15.5" cy="10" r="2" fill={knob} />
    </Svg>
  );
}

export const TallyNavIcons = {
  Home: HomeIcon,
  Ranking: RankingIcon,
  NewGame: NewGameIcon,
  Sessions: SessionsIcon,
  Profile: ProfileIcon,
  LiveGame: LiveGameIcon,
  Jackpot: JackpotIcon,
  Settings: SettingsIcon,
};

export default TallyNavIcons;
